import {delegateToDataAttribute} from '@util/EventUtils';
import mediaQuery from '@util/media-queries';

const DEFAULT_OPTIONS = {
    zoomScale: 3,
    previewInsertionNode: null // default to parent
};

export default function ImageZoom ( scope = document, options ) {

    const {zoomScale, previewInsertionNode} = {
        ...DEFAULT_OPTIONS,
        ...options
    };

    let target = null;
    let lens = null;
    let preview = null;
    let previewError = null;

    function init () {
        return addListeners();
    }

    function createLens ( target ) {
        if ( !preview ) {
            return;
        }

        lens = document.createElement( 'div' );
        lens.classList.add( 'image-zoom-lens' );
        lens.style.width = `${preview.offsetWidth / zoomScale}px`;
        lens.style.height = `${preview.offsetHeight / zoomScale}px`;

        target.parentElement.insertBefore( lens, target );
    }

    function destroyLens () {
        lens.remove();
        lens = null;
    }

    function destroyPreviewPane () {
        preview.remove();
        preview = null;
    }

    function preloadImage ( src, onLoad ) {

        return new Promise( ( resolve, reject ) => {
            const preload = new Image();
            preload.addEventListener( 'load', () => {
                typeof onLoad == 'function' && onLoad();
                resolve();
            } );
            preload.onerror = reject
            preload.src = src;
        } );
    }

    function createPreviewPane () {
        const box = target.getBoundingClientRect();
        const el = document.createElement( 'div' );
        el.classList.add( 'image-zoom-preview', 'shadow-md' );
        el.style.width = '100%';
        el.style.maxWidth = `${box.width}px`;
        el.style.height = '100%';
        el.style.maxHeight = `${box.height}px`;

        return el;
    }

    async function configureDesktopPreview ( target, previewSrc ) {
        try {
            console.warn( '[ImageZoom] create Preview' );
            preview = createPreviewPane();

            // check that original source can be loaded
            await preloadImage( previewSrc );

            const srcUrl = new URL( previewSrc );
            srcUrl.searchParams.set( 'w', '2400' );
            srcUrl.searchParams.set( 'fm', 'webp' );
            const insertionNode = previewInsertionNode || target.parentElement;
            const box = target.getBoundingClientRect();

            insertionNode.prepend( preview );

            // preview.classList.add( 'loading' );
            preloadImage( srcUrl.href, () => preview?.classList.remove( 'loading' ) );
            preview.style.backgroundImage = `url(${srcUrl.href})`;
            preview.style.backgroundSize = (box.width * zoomScale) + 'px ' + (box.height * zoomScale) + 'px';
        }
        catch ( err ) {
            console.warn( '[ImageZoom] - Image preview missing or invalid src' )
            previewError = true;
        }
    }

    async function onMouseMove ( e ) {
        if ( window.matchMedia( mediaQuery.max['screenMedium'] ).matches ) {
            console.log( '[mediaQuery] - mobile device ignored' );
            return;
        }
        if ( previewError ) {
            return;
        }

        if ( !preview ) {
            target = e.target;
            const imageSrc = target.currentSrc || target.src;

            if ( target.parentElement.nodeName.toLowerCase() === 'picture' ) {
                target = target.parentElement;
            }

            await configureDesktopPreview( target, imageSrc );
            createLens( target );

            target.addEventListener( 'mouseleave', function onLeave () {
                console.warn( '[ImageZoom] destroy Preview' );
                target.removeEventListener( 'mouseleave', onLeave );
                destroyPreviewPane();
                destroyLens();
                previewError = null;
                target = null;
            } );
        }

        e.preventDefault();
        const pos = getCursorPos( e );
        moveLens( pos );
    }

    function moveLens ( pos ) {

        const box = target.getBoundingClientRect();

        if ( !lens ) {
            return;
        }
        // calculate the position of the lens
        let x = pos.x - (lens.offsetWidth / 2);
        let y = pos.y - (lens.offsetHeight / 2);

        // keep lens inside image frame
        if ( x > box.width - lens.offsetWidth ) {
            x = box.width - lens.offsetWidth;
        }
        if ( x < 0 ) {
            x = 0;
        }
        if ( y > box.height - lens.offsetHeight ) {
            y = box.height - lens.offsetHeight;
        }
        if ( y < 0 ) {
            y = 0;
        }

        // position lens
        lens.style.left = x + 'px';
        lens.style.top = y + 'px';

        // display what the lens "sees"
        preview.style.backgroundPosition = '-' + (x * zoomScale) + 'px -' + (y * zoomScale) + 'px';
    }

    function getCursorPos ( e ) {
        let a;
        let x = 0;
        let y = 0;
        a = target.getBoundingClientRect();

        // cursor position relative to image
        x = e.pageX - a.left;
        y = e.pageY - a.top;

        // adjust for scroll offset
        x = x - window.pageXOffset;
        y = y - window.pageYOffset;

        return {x: x, y: y};
    }

    function addListeners () {
        const handleMouseMove = delegateToDataAttribute( 'imageZoom', onMouseMove );

        scope.addEventListener( 'mousemove', handleMouseMove );

        return () => {
            scope.removeEventListener( 'mousemove', handleMouseMove );
        }
    }

    return {init};
}